import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { auth } from '../lib/firebase';
import { TopNav } from '../components/TopNav';
import { UserCheck, Users, Clock, CircleAlert, ChevronLeft, Zap, CircleCheck as CheckCircle2, MoreVertical, Search, DollarSign, X } from 'lucide-react';
import clsx from 'clsx';
import { useLanguage } from '../contexts/LanguageContext';

export const HospitalityDashboard = () => {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isOwner, setIsOwner] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [newRegIds, setNewRegIds] = useState(new Set());
    const [error, setError] = useState(null);
    const [checkInModal, setCheckInModal] = useState({ isOpen: false, regId: null, buyIn: 1000 });
    const { t, direction, language, formatCurrency } = useLanguage();
    
    // Animation/State management for "New" badges
    const fadeTimeoutRef = useRef({});

    const user = auth.currentUser;

    useEffect(() => {
        if (!sessionId || !user) return;

        console.log('[Hospitality] Initializing Live Feed for Session:', sessionId);

        const fetchSessionAndData = async () => {
            try {
                // 1. Verify Ownership
                const { data: session, error: sessionError } = await supabase
                    .from('sessions')
                    .select('host_id')
                    .eq('session_id', sessionId)
                    .single();

                if (sessionError) throw sessionError;
                
                // If session exists but no host_id, we allow (legacy)
                // If host_id exists, must match
                if (session.host_id && session.host_id !== user.uid) {
                    setIsOwner(false);
                    setError(t('accessRestricted'));
                    return;
                }
                
                setIsOwner(true);

                // 2. Initial Load of the View
                const { data, error: fetchError } = await supabase
                    .from('active_session_view')
                    .select('*')
                    .eq('session_id', sessionId)
                    .order('registered_at', { ascending: false });

                if (fetchError) throw fetchError;
                setRegistrations(data || []);
            } catch (err) {
                console.error('[Hospitality Load Error]', err);
                setError(t('errorLoading')); // Note: Added to translations if missing, or use fallback
            } finally {
                setLoading(false);
            }
        };

        fetchSessionAndData();

        // 2. Realtime Subscription
        // Note: We listen to 'session_registrations' but join data manually or refetch
        const subscription = supabase
            .channel(`session-${sessionId}`)
            .on(
                'postgres_changes', 
                { 
                    event: '*', 
                    schema: 'public', 
                    table: 'session_registrations',
                    filter: `session_id=eq.${sessionId}` 
                }, 
                async (payload) => {
                    console.log('[Hospitality Realtime Event]', payload.eventType);
                    
                    if (payload.eventType === 'INSERT') {
                        // Fetch the full view record for the new registration
                        const { data: newRec } = await supabase
                            .from('active_session_view')
                            .select('*')
                            .eq('registration_id', payload.new.id)
                            .single();
                        
                        if (newRec) {
                            setRegistrations(prev => [newRec, ...prev]);
                            
                            // Mark as new for animation
                            setNewRegIds(prev => new Set([...prev, newRec.registration_id]));
                            
                            // Cleanup badge after 5s
                            setTimeout(() => {
                                setNewRegIds(prev => {
                                    const next = new Set(prev);
                                    next.delete(newRec.registration_id);
                                    return next;
                                });
                            }, 5000);
                        }
                    } else if (payload.eventType === 'UPDATE') {
                        setRegistrations(prev => prev.map(r => 
                            r.registration_id === payload.new.id 
                                ? { ...r, status: payload.new.status, buy_in_amount: payload.new.buy_in_amount, rake_paid: payload.new.rake_paid, cash_out_amount: payload.new.cash_out_amount } 
                                : r
                        ));
                    } else if (payload.eventType === 'DELETE') {
                        setRegistrations(prev => prev.filter(r => r.registration_id !== payload.old.id));
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(subscription);
            // Clear any pending timeouts
            Object.values(fadeTimeoutRef.current).forEach(clearTimeout);
        };
    }, [sessionId, user]);

    const openCheckInModal = (regId) => {
        setCheckInModal({ isOpen: true, regId, buyIn: 1000 });
    };

    const confirmCheckIn = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const { regId, buyIn } = checkInModal;
        try {
            const { error: updateError } = await supabase
                .from('session_registrations')
                .update({ status: 'seated', buy_in_amount: Number(buyIn) || 0 })
                .eq('id', regId);

            if (updateError) throw updateError;
            
            // UI will update via Realtime, but pessimistic update for speed
            setRegistrations(prev => prev.map(r => 
                r.registration_id === regId ? { ...r, status: 'seated', buy_in_amount: Number(buyIn) || 0 } : r
            ));
            
            setCheckInModal({ isOpen: false, regId: null, buyIn: 1000 });
        } catch (err) {
            console.error('[Hospitality Check-in Error]', err);
            alert(t('errorGeneric'));
        }
    };

    const filteredPlayers = registrations.filter(p => 
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        p.phone?.includes(searchTerm)
    );

    const stats = {
        pot: registrations.reduce((sum, r) => sum + (r.status === 'seated' ? (Number(r.buy_in_amount) || 0) : 0), 0),
        rake: registrations.reduce((sum, r) => sum + (Number(r.rake_paid) || 0), 0),
        seated: registrations.filter(r => r.status === 'seated').length
    };

    return (
        <div className="flex flex-col min-h-screen bg-slate-950 text-white font-outfit overflow-hidden">
            <TopNav />
            
            <main className="flex-1 flex flex-col w-full max-w-4xl mx-auto px-6 py-8 overflow-hidden">
                {error ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center">
                        <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mb-6 border border-red-500/20">
                            <CircleAlert className="w-10 h-10 text-red-500" />
                        </div>
                        <h2 className="text-2xl font-black mb-2">{t('accessRestricted')}</h2>
                        <p className="text-slate-500 max-w-xs mx-auto mb-8 font-bold">{error}</p>
                        <button 
                            onClick={() => navigate('/dashboard')}
                            className="bg-slate-900 border border-white/5 px-8 py-3 rounded-2xl font-black hover:bg-slate-800 transition-all"
                        >
                            {t('backToDashboard')}
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Header Section */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <button onClick={() => navigate(-1)} className={clsx("p-2 text-slate-500 hover:text-white transition-colors", direction === 'rtl' ? "-mr-2" : "-ml-2")}>
                                <ChevronLeft className={clsx("w-5 h-5", direction === 'rtl' ? "rotate-180" : "")} />
                            </button>
                            <h1 className="text-3xl font-black text-amber-500 font-heebo tracking-tighter">{t('hospitalityTitle')}</h1>
                        </div>
                        <p className={clsx("text-sm text-slate-500 font-bold uppercase tracking-widest", direction === 'rtl' ? "mr-10" : "ml-10")}>{t('activeSessionFeed')}</p>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 bg-slate-900 border border-white/5 rounded-2xl px-4 py-2">
                            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('liveData')}</span>
                        </div>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                    {[
                        { label: t('totalPot'), val: formatCurrency(stats.pot), icon: Zap, color: 'amber' },
                        { label: t('totalRake'), val: formatCurrency(stats.rake), icon: DollarSign, color: 'emerald' },
                        { label: t('activeSeats'), val: stats.seated, icon: Users, color: 'slate' }
                    ].map((s, i) => (
                        <div key={i} className="bg-slate-900/40 border border-white/5 rounded-3xl p-5 flex flex-col items-center justify-center relative group overflow-hidden">
                             <div className={`absolute top-0 right-0 w-20 h-20 bg-${s.color}-500/5 blur-2xl -mr-10 -mt-10 rounded-full`} />
                             <s.icon className={`w-5 h-5 text-${s.color}-500/50 mb-2`} />
                             <div className="text-2xl font-black">{s.val}</div>
                             <div className="text-[9px] font-black text-slate-600 uppercase tracking-widest mt-1">{s.label}</div>
                        </div>
                    ))}
                </div>

                {/* Search & List */}
                <div className="flex-1 flex flex-col min-h-0 bg-slate-900/40 border border-white/5 rounded-[3rem] p-4 relative">
                    <div className="px-4 pt-4 pb-6 border-b border-white/5 flex items-center justify-between">
                        <div className={clsx("relative flex-1 max-w-sm", direction === 'rtl' ? "pl-4" : "pr-4")}>
                            <Search className={clsx("absolute top-1/2 -translate-y-1/2 w-4 h-4 text-slate-700", direction === 'rtl' ? "right-4" : "left-4")} />
                            <input 
                                type="text"
                                placeholder={t('searchNamePhone')}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className={clsx(
                                    "w-full bg-slate-950/50 border border-white/5 rounded-2xl py-3 text-sm focus:border-amber-500/50 outline-none transition-all placeholder:text-slate-800",
                                    direction === 'rtl' ? "pr-12 pl-4" : "pl-12 pr-4"
                                )}
                            />
                        </div>
                        <div className={clsx("text-[10px] font-black text-slate-700 uppercase tracking-widest hidden sm:block", direction === 'rtl' ? "mr-4" : "ml-4")}>
                            {t('sessions')} // {sessionId?.split('-')[0]}
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-2 py-4 space-y-3 custom-scrollbar">
                        {loading ? (
                            <div className="flex items-center justify-center h-40">
                                <RotateCw className="w-8 h-8 text-amber-500 animate-spin" />
                            </div>
                        ) : filteredPlayers.length === 0 ? (
                            <div className="text-center py-20 text-slate-700 uppercase font-black text-xs tracking-widest">No Active Registrations</div>
                        ) : filteredPlayers.map((reg) => {
                            let loyaltyBadge = null;
                            if (reg.total_sessions >= 20 || reg.loyalty_score >= 100) {
                                loyaltyBadge = { label: t('whale'), color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' };
                            } else if (reg.total_sessions >= 5) {
                                loyaltyBadge = { label: t('regular'), color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
                            } else {
                                loyaltyBadge = { label: t('new'), color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' };
                            }

                            return (
                            <div 
                                key={reg.registration_id}
                                className={clsx(
                                    "group relative flex items-center justify-between p-5 rounded-[2rem] border transition-all duration-700",
                                    newRegIds.has(reg.registration_id) 
                                        ? "bg-amber-500/10 border-amber-500/30 animate-slide-in" 
                                        : "bg-slate-900 border-white/5 hover:border-white/10"
                                )}
                            >
                                <div className="flex items-center gap-5">
                                    <div className="relative">
                                        <div className="w-12 h-12 bg-slate-800 rounded-2xl flex items-center justify-center border border-white/5 text-slate-400 text-sm font-black">
                                            {reg.name?.charAt(0)}
                                        </div>
                                        {reg.status === 'seated' && (
                                            <div className="absolute -bottom-1 -left-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-slate-900 flex items-center justify-center">
                                                <CheckCircle2 className="w-3 h-3 text-white" />
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-black text-white text-lg leading-none">{reg.name}</h3>
                                            {newRegIds.has(reg.registration_id) && (
                                                <span className="text-[9px] font-black bg-amber-500 text-slate-950 px-2 py-0.5 rounded-full uppercase animate-pulse">{t('new')}</span>
                                            )}
                                            {loyaltyBadge && (
                                                <span className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase border ${loyaltyBadge.color}`}>
                                                    {loyaltyBadge.label}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[10px] text-slate-600 font-bold mt-1 tabular-nums">{reg.phone}</div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    <div className={direction === 'rtl' ? "text-left" : "text-right"}>
                                        <div className="text-[9px] text-slate-700 font-black uppercase tracking-widest mb-1">{t('time')}</div>
                                        <div className="text-xs font-bold text-slate-500 tabular-nums">
                                            {new Date(reg.registered_at).toLocaleTimeString(language === 'he' ? 'he-IL' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>

                                    {reg.status !== 'seated' ? (
                                        <button 
                                            onClick={() => openCheckInModal(reg.registration_id)}
                                            className="px-6 py-2.5 bg-amber-500/10 hover:bg-amber-500 text-amber-500 hover:text-slate-950 border border-amber-500/20 rounded-xl text-xs font-black transition-all active:scale-95"
                                        >
                                            {t('seatAtTable')}
                                        </button>
                                    ) : (
                                        <div className="px-6 py-2.5 bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 rounded-xl text-xs font-black">
                                            {t('seated')}
                                        </div>
                                    )}
                                    
                                    <button className="p-2 text-slate-800 hover:text-white transition-colors">
                                        <MoreVertical className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )})}
                    </div>
                </div>

                {checkInModal.isOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200" dir="rtl">
                        <form onSubmit={confirmCheckIn} className="bg-slate-900 border border-white/10 rounded-[2rem] w-full max-w-sm p-6 shadow-2xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/10 blur-3xl rounded-full -mr-16 -mt-16" />
                            
                            <button 
                                type="button"
                                onClick={() => setCheckInModal({ isOpen: false, regId: null, buyIn: 1000 })}
                                className="absolute top-5 left-5 p-2 text-slate-500 hover:text-white transition-colors z-10 bg-slate-800/50 rounded-full"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            <h3 className="text-2xl font-black text-white mb-1 font-heebo">{t('seatPlayer')}</h3>
                            <p className="text-xs text-slate-500 font-bold mb-6">{t('seatPlayerInstructions')}</p>

                            <div className="mb-6 relative">
                                <span className={clsx("absolute top-1/2 -translate-y-1/2 text-slate-500 font-bold text-lg", direction === 'rtl' ? "right-4" : "left-4")}>₪</span>
                                <input 
                                    type="number" 
                                    min="0"
                                    value={checkInModal.buyIn}
                                    onChange={(e) => setCheckInModal(prev => ({ ...prev, buyIn: e.target.value }))}
                                    className={clsx(
                                        "w-full bg-slate-950/50 border border-white/10 rounded-2xl py-4 text-xl font-black text-white focus:border-amber-500 outline-none transition-all tabular-nums",
                                        direction === 'rtl' ? "pr-10 pl-4" : "pl-10 pr-4"
                                    )}
                                    dir="ltr"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button 
                                    type="button"
                                    onClick={() => setCheckInModal({ isOpen: false, regId: null, buyIn: 1000 })}
                                    className="flex-1 py-3.5 bg-slate-800 text-white rounded-xl font-black hover:bg-slate-700 transition-colors"
                                >
                                    {t('cancel')}
                                </button>
                                <button 
                                    type="submit"
                                    className="flex-1 py-3.5 bg-amber-500 text-slate-950 rounded-xl font-black hover:bg-amber-400 transition-colors"
                                >
                                    {t('confirmSeating')}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
                    </>
                )}
            </main>

            <style>{`
                @keyframes slideIn {
                    from { transform: translateX(50px); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                .animate-slide-in {
                    animation: slideIn 0.6s cubic-bezier(0.23, 1, 0.32, 1) forwards;
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 10px;
                }
            `}</style>
        </div>
    );
};

const RotateCw = ({ className }) => (
    <Zap className={clsx(className, "animate-spin")} />
);
